import type {
  InventoryArticleEntry,
  InventoryEntry,
  InventoryStage,
} from '../../../scripts/support-inventory.mjs';

import inventoryJson from '../../../../docs/support/inventory.json';
import stagesJson from '../../../../docs/support/stages.json';

/**
 * The help site's content, read at build time from docs/support/.
 *
 * This is the one module that knows where the content lives. The inventory
 * and the stages are bundled as JSON, so what the site shows is decided when
 * it is built: publishing an article is the commit that moves its status to
 * `written`. Articles are separate chunks, fetched when their page opens;
 * screenshots are ordinary hashed assets.
 *
 * docs/support/ is four directory levels above this file. Vite requires glob
 * patterns to be literal, so the prefix is repeated rather than composed.
 */
const articleModules = import.meta.glob<string>('../../../../docs/support/articles/*.md', {
  import: 'default',
  query: '?raw',
});
const screenshotModules = import.meta.glob<string>(
  '../../../../docs/support/screenshots/*.png',
  { eager: true, import: 'default', query: '?url' },
);

export type SupportAudience = 'instructor' | 'student';
export type SupportEntry = InventoryArticleEntry;
export type SupportStage = InventoryStage;

export const ARTICLES_RELATIVE_PATH = 'docs/support/articles';

/**
 * Whether unpublished entries render, with their review status. Read from the
 * build flag; a build without it lists only `written` entries. Draft-only UI
 * is loaded behind the same check at module scope, so it is absent from a
 * production bundle (proven by the build check in the change's tasks).
 */
export function isDraftMode(): boolean {
  return import.meta.env.VITE_SUPPORT_DRAFTS === '1';
}

const entries = (inventoryJson as unknown as InventoryEntry[]).filter(
  (entry): entry is SupportEntry => entry.internal !== true,
);
const stages = stagesJson as SupportStage[];

export function supportEntries(): SupportEntry[] {
  return entries;
}

export function supportStages(): SupportStage[] {
  return stages;
}

export function findStage(id: string): SupportStage | undefined {
  return stages.find((stage) => stage.id === id);
}

export function findEntry(slug: string): SupportEntry | undefined {
  return entries.find((entry) => entry.slug === slug);
}

/** Public when written; everything else only while reviewing drafts. */
export function isPublished(entry: SupportEntry): boolean {
  return entry.status === 'written' || isDraftMode();
}

export function publishedEntries(audience: SupportAudience): SupportEntry[] {
  return entries.filter((entry) => entry.audience === audience && isPublished(entry));
}

export interface SupportIndexSection {
  entries: SupportEntry[];
  /** Which journey the stage belongs to; instructors see both. */
  journey: SupportAudience;
  stage: SupportStage;
}

/**
 * The index for an audience: one section per stage in journey order, empty
 * stages dropped. Instructors get the student journey first, because they are
 * asked about it, then their own.
 */
export function indexSections(audience: SupportAudience): SupportIndexSection[] {
  const journeys: SupportAudience[] =
    audience === 'instructor' ? ['student', 'instructor'] : ['student'];

  return journeys.flatMap((journey) => {
    const published = publishedEntries(journey);

    return stages
      .filter((stage) => stage.audience === journey)
      .map((stage) => ({
        entries: published.filter((entry) => entry.stage === stage.id),
        journey,
        stage,
      }))
      .filter((section) => section.entries.length > 0);
  });
}

export function indexPath(audience: SupportAudience): string {
  return audience === 'instructor' ? '/help/instructors' : '/help';
}

export function indexTitle(audience: SupportAudience): string {
  return audience === 'instructor' ? 'Help for instructors' : 'Help for students';
}

export function articlePath(entry: Pick<SupportEntry, 'slug'>): string {
  return `/help/${entry.slug}`;
}

function fileName(modulePath: string): string {
  return modulePath.slice(modulePath.lastIndexOf('/') + 1);
}

const screenshotUrlByFile = new Map(
  Object.entries(screenshotModules).map(([modulePath, url]) => [fileName(modulePath), url]),
);
const articleLoaderByFile = new Map(
  Object.entries(articleModules).map(([modulePath, loader]) => [fileName(modulePath), loader]),
);

/** The bundled URL of a file under docs/support/screenshots/, if it exists. */
export function screenshotUrl(file: string): string | undefined {
  return screenshotUrlByFile.get(file);
}

export function variantFile(file: string, variant: 'dark' | 'light' | 'mobile'): string {
  const base = file.replace(/\.png$/, '');

  return variant === 'light' ? `${base}.png` : `${base}.${variant}.png`;
}

export interface ResolvedScreenshot {
  /** The inventory description: the one place alt text lives. */
  alt: string;
  /** The 390px capture, served on phones when it exists. */
  mobileSrc?: string;
  src: string;
}

/**
 * A screenshot an article may show: it must be declared on the entry (that is
 * where its alt text comes from) and its light file must be bundled.
 */
export function resolveScreenshot(
  entry: SupportEntry,
  file: string,
): ResolvedScreenshot | undefined {
  const declared = entry.screenshots.find((shot) => shot.file === file);
  const src = screenshotUrl(file);

  if (!declared || !src) {
    return undefined;
  }

  const mobileSrc = screenshotUrl(variantFile(file, 'mobile'));

  return { alt: declared.description, src, ...(mobileSrc ? { mobileSrc } : {}) };
}

function articleFileName(entry: SupportEntry): string {
  return entry.article ? fileName(entry.article) : `${entry.slug}.md`;
}

export function hasArticleFile(entry: SupportEntry): boolean {
  return articleLoaderByFile.has(articleFileName(entry));
}

/** The article's Markdown, loaded as its own chunk. */
export async function loadArticle(entry: SupportEntry): Promise<string> {
  const loader = articleLoaderByFile.get(articleFileName(entry));

  if (!loader) {
    throw new Error(
      `No article file for \`${entry.slug}\` at ${ARTICLES_RELATIVE_PATH}/${articleFileName(entry)}.`,
    );
  }

  return loader();
}
